import { AppDispatch } from "../../state-management/store";
import { FormikHelpers } from "formik";
import * as yup from "yup";
import {
  IFormValues,
  ISearchFormValues,
  OutageSchemaType,
  PinModeSchemaType,
  SearchSchemaType,
} from "./Form.types";
import { OutageViewModel } from "../../api/generated/models";
import {
  searchOutages,
  submitOutage,
} from "../../state-management/slices/outages/outages.thunk";
import { getGlobalState } from "../../shared/utils/testing.utils";
import { IGTMEventFunction } from "../../shared/types/logging.types";
import FormUtils from "./Form.utils";
import { AnyObject, Assign, ObjectShape } from "yup/lib/object";
import { RequiredStringSchema } from "yup/lib/string";

/**
 * FormService
 * @export
 * @class FormService
 */
export class FormService {
  private static instance: FormService;
  private dispatch: AppDispatch;
  private sendDataToGTM: IGTMEventFunction;
  private submitFormActions!: FormikHelpers<IFormValues>;
  private searchFormActions!: FormikHelpers<ISearchFormValues>;
  private formikReference!: any;

  /**
   * Creates an instance of FormService.
   * @param {AppDispatch} dispatch
   * @param {IGTMEventFunction} sendDataToGTM
   * @memberof FormService
   */
  private constructor(dispatch: AppDispatch, sendDataToGTM: IGTMEventFunction) {
    this.dispatch = dispatch;
    this.sendDataToGTM = sendDataToGTM;
  }

  /**
   * getInstance
   * @static
   * @param {AppDispatch} dispatch
   * @return {*}  {FormService}
   * @memberof FormService
   */
  public static getInstance(
    dispatch: AppDispatch,
    sendDataToGTM: IGTMEventFunction
  ): FormService {
    if (!FormService.instance) {
      FormService.instance = new FormService(dispatch, sendDataToGTM);
    }
    return FormService.instance;
  }

  /**
   * submitOutageReport
   * @param {*} values
   * @param {*} { setSubmitting, resetForm }
   * @memberof FormService
   */
  public submitOutageReport(
    values: IFormValues,
    actions: FormikHelpers<IFormValues>
  ): void {
    this.submitFormActions = actions;
    this.dispatch(submitOutage(values, actions, this.sendDataToGTM));
  }

  /**
   * searchOutages
   * @param {ISearchFormValues} values
   * @param {FormikHelpers<ISearchFormValues>} actions
   * @memberof FormService
   */
  public searchOutages(
    values: ISearchFormValues,
    actions: FormikHelpers<ISearchFormValues>
  ): void {
    this.searchFormActions = actions;
    this.dispatch(searchOutages(values, actions, this.sendDataToGTM));
  }

  /**
   * getValidationSchema
   * @return {*}  {yup.ObjectSchema<any>}
   * @memberof FormService
   */
  public getValidationSchema():
    | PinModeSchemaType
    | SearchSchemaType
    | OutageSchemaType {
    const { pinMode, searchMode } = getGlobalState().ui;

    if (pinMode.enabled) {
      return FormUtils.pinModeSchema;
    }

    if (searchMode.enabled) {
      return FormUtils.searchSchema;
    }

    return FormUtils.outageSchema;
  }

  /**
   * getReportedDate
   * @param {(OutageViewModel | null)} outage
   * @return {*}  {(string | null)}
   * @memberof FormService
   */
  public getReportedDate(outage: OutageViewModel | null): string | null {
    if (!outage || !outage.reportedDateTime) {
      return null;
    }

    const reportedDate = new Date(outage.reportedDateTime);
    return reportedDate.toLocaleDateString("en-US");
  }

  /**
   * getReportedTime
   * @param {(OutageViewModel | null)} outage
   * @return {*}  {(string | null)}
   * @memberof FormService
   */
  public getReportedTime(outage: OutageViewModel | null): string | null {
    if (!outage || !outage.reportedDateTime) {
      return null;
    }

    const reportedDate = new Date(outage.reportedDateTime);
    return reportedDate.toLocaleTimeString("en-US");
  }

  public resetForm = (): void => {
    if (this.formikReference.current) {
      this.formikReference.current.resetForm();
    } else {
      return;
    }
  };

  public setFormikReference = (ref: any): void => {
    this.formikReference = ref;
  };
}
